import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  HStack,
  IconButton,
  Input,
  SkeletonText,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  VStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { EditorContent } from "@tiptap/react";
import { useEffect, useState } from "react";
import { RiMenuLine, RiMore2Line } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/chakra/Breadcrumbs";
import { useApiMutation, useApiQuery } from "../../../utilities/apibelRequest";
import useToast from "../../../utilities/useToast";
import NotFoundScreen from "../../NotFound";
import ManualSectionDetailsCard from "./components/ManualSectionDetailsCard";
import { RichTextToolbar } from "./components/RichTextToolbar";
import { UsageManualSectionsLink } from "./components/UsageManualSectionsLink";
import { useEditorWithExtensions } from "./utils/useEditorWithExtensions";

export default function EditManualSectionScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { sectionID } = params;
  const [title, setTitle] = useState("Section");

  const drawerDisclosure = useDisclosure();

  const isMobile = useBreakpointValue({ base: true, lg: false });
  const contentWidth = useBreakpointValue({
    base: "100vw",
    lg: "calc(100vw - 332px)",
  });
  const headerHeight = useBreakpointValue({
    base: "64px",
    lg: "48px",
  });

  const editor = useEditorWithExtensions({
    content: "",
    placeholder: "Click here to start typing",
    limit: 20000,
    editable: true,
  });

  const [usedTitle, setUsedTitle] = useState<string | null>(null);
  const [usedContent, setUsedContent] = useState<string | null>(null);

  const sectionQuery = useApiQuery(
    "manual/sectionByID",
    { manualSectionID: sectionID || "" },
    {
      enabled: !!sectionID,
    },
  );

  useEffect(() => {
    if (sectionQuery.isSuccess && editor) {
      if (usedTitle !== sectionQuery.data.title) {
        setTitle(sectionQuery.data.title);
        setUsedTitle(sectionQuery.data.title);
      }
      if (
        usedContent !== sectionQuery.data.content.jsonStr &&
        sectionQuery.data.content.jsonStr
      ) {
        try {
          editor.commands.setContent(
            JSON.parse(sectionQuery.data.content.jsonStr),
          );
          setUsedContent(sectionQuery.data.content.jsonStr);
        } catch (e) {
          console.error("Failed to set content", e);
        }
      }
    }
  }, [
    editor,
    sectionQuery.data?.content.jsonStr,
    sectionQuery.data?.title,
    usedContent,
    usedTitle,
    sectionQuery.isSuccess,
  ]);

  const { isFetching } = sectionQuery;

  const updateSectionMutation = useApiMutation("manual/updateSection");
  const updateSectionUserMutation = useApiMutation(
    "manual/updateManualSectionResponsibleUser",
  );

  const didTitleChange = sectionQuery.isSuccess && title !== usedTitle;
  const didContentChange =
    sectionQuery.isSuccess && JSON.stringify(editor?.getJSON()) !== usedContent;
  const didChange = didTitleChange || didContentChange;

  const handleConfirmCancel = () => {
    if (!didChange) {
      return true;
    }
    const didConfirm = window.confirm(
      "Are you sure you want to leave, unsaved changes will be lost",
    );
    return didConfirm;
  };

  const handleClose = () => {
    if (sectionQuery.isSuccess) {
      navigate(`/manual/${sectionQuery.data.manual.manualID}`);
    } else {
      navigate("/manual");
    }
  };

  const handleClickCancel = () => {
    if (!handleConfirmCancel()) {
      return;
    }
    handleClose();
  };

  const handleClickSave = async () => {
    if (!didChange) {
      return false;
    }

    const newJson = JSON.stringify(editor?.getJSON());
    const newText = editor?.getText();
    const newTitle = title;

    if (!sectionID || !sectionQuery.isSuccess) {
      return false;
    }

    const newSection = {
      manualSectionID: sectionID,
      title: didTitleChange ? newTitle : undefined,
      content: didContentChange
        ? { jsonString: newJson, rawPreview: (newText || "").slice(0, 512) }
        : undefined,
    };

    editor?.setEditable(false);

    try {
      await updateSectionMutation.mutateAsync({ section: newSection });
    } catch (e) {
      console.error("Failed to update section", e);
      toast.displayToast({
        title: "Failed to update section",
        status: "error",
      });
      return false;
    }
    editor?.setEditable(true);

    toast.displayToast({
      title: "Updated section",
      status: "success",
    });
    return true;
  };

  const handleClickSaveAndClose = async () => {
    const didComplete = await handleClickSave();
    if (didComplete) {
      queryClient.invalidateQueries({ queryKey: ["manual"] });
      handleClose();
    }
  };

  const isMutating =
    updateSectionMutation.isPending || updateSectionUserMutation.isPending;

  const linkedQuery = useApiQuery(
    "manual/manyRecordsForLinkedSection",
    {
      sectionID: sectionID || "",
    },
    {
      enabled: sectionID !== undefined,
    },
  );

  const isLoading = linkedQuery.isLoading || sectionQuery.isLoading;

  if (!sectionID) {
    return <NotFoundScreen />;
  }

  return (
    <chakra-scope>
      <Box w={contentWidth}>
        <Box
          pos="fixed"
          top="top-bar"
          w={contentWidth}
          h={headerHeight}
          zIndex={100}
          p="4"
          bg="white">
          <VStack w="full" align="start" spacing="0" h="100%">
            {isMobile ? (
              <HStack
                w="full"
                justify="space-between"
                align="center"
                minH="0"
                flex="1">
                <HStack spacing={1} flex="1" minW="0">
                  <Breadcrumbs
                    breadcrumbs={[
                      { link: "/manual", label: "Manual" },
                      ...(sectionQuery.isSuccess
                        ? [
                            {
                              link: `/manual/${sectionQuery.data.manual.manualID}`,
                              label:
                                sectionQuery.data.manual.name.length > 15
                                  ? `${sectionQuery.data.manual.name.slice(0, 15)}...`
                                  : sectionQuery.data.manual.name,
                            },
                          ]
                        : []),
                    ]}
                  />
                  <Heading
                    as="h1"
                    size="sm"
                    color="gray.700"
                    noOfLines={1}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxW="100%"
                    title={title.length >= 1 ? title : "Untitled Section"}>
                    {title.length > 25
                      ? `${title.slice(0, 25)}...`
                      : title.length >= 1
                        ? title
                        : "Untitled Section"}
                  </Heading>
                </HStack>

                <Menu>
                  <MenuButton
                    as={IconButton}
                    size="xs"
                    icon={<RiMore2Line />}
                    isDisabled={isMutating || isFetching}
                    variant="ghost"
                    aria-label="More actions"
                  />
                  <MenuList>
                    <MenuItem
                      isDisabled={isMutating || isFetching}
                      onClick={handleClickCancel}>
                      Cancel
                    </MenuItem>
                    <MenuItem
                      isDisabled={!didChange || isMutating || isFetching}
                      onClick={handleClickSave}>
                      Save
                    </MenuItem>
                    <MenuItem
                      isDisabled={!didChange || isMutating || isFetching}
                      onClick={handleClickSaveAndClose}>
                      Save & Close
                    </MenuItem>
                  </MenuList>
                </Menu>
                <IconButton
                  icon={<RiMenuLine size="18" />}
                  variant="ghost"
                  size="sm"
                  onClick={drawerDisclosure.onOpen}
                  aria-label="Open details"
                />
              </HStack>
            ) : (
              <HStack justify="space-between" w="full" h="100%">
                <HStack
                  align="center"
                  spacing={2}
                  flex="1"
                  minW="0"
                  _hover={{
                    "& .edit-button": { visibility: "visible", opacity: 1 },
                  }}>
                  <HStack spacing={0} flex="1" minW="0">
                    <Breadcrumbs
                      breadcrumbs={[
                        { link: "/manual", label: "Manual" },
                        ...(sectionQuery.isSuccess
                          ? [
                              {
                                link: `/manual/${sectionQuery.data.manual.manualID}`,
                                label: sectionQuery.data.manual.name,
                              },
                            ]
                          : []),
                      ]}
                    />
                    <Heading
                      as="h1"
                      size="md"
                      color="gray.700"
                      noOfLines={1}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      maxW="100%"
                      title={title.length >= 1 ? title : "Untitled Section"}>
                      {title.length >= 1 ? title : "Untitled Section"}
                    </Heading>
                  </HStack>
                </HStack>

                <HStack h="100%" spacing={1}>
                  <ButtonGroup size="sm">
                    <Button
                      size="sm"
                      isDisabled={isMutating || isFetching}
                      onClick={handleClickCancel}
                      colorScheme="gray">
                      Cancel
                    </Button>

                    <Tooltip
                      label={
                        didChange
                          ? "Save and continue editing the section"
                          : "Nothing modified"
                      }>
                      <Button
                        isDisabled={!didChange || isMutating || isFetching}
                        onClick={handleClickSave}>
                        Save
                      </Button>
                    </Tooltip>
                    <Tooltip
                      label={
                        didChange
                          ? "Save and return to the manual"
                          : "Nothing modified"
                      }>
                      <Button
                        isDisabled={!didChange || isMutating || isFetching}
                        onClick={handleClickSaveAndClose}>
                        Save & Close
                      </Button>
                    </Tooltip>
                  </ButtonGroup>
                </HStack>
              </HStack>
            )}
          </VStack>
        </Box>

        <Box minH="80px" w="100%" px="1" pos="relative">
          <Box
            zIndex="1"
            bg="white"
            borderBottomWidth="1px"
            borderTopWidth="1px"
            w={contentWidth}
            top={`calc(var(--chakra-space-top-bar) + ${headerHeight})`}
            left="0"
            position="fixed">
            <RichTextToolbar editor={editor} />
          </Box>

          <Box pt={useBreakpointValue({ base: "32", lg: "24" })} w="100%">
            <Box
              maxW="container.lg"
              m="auto"
              px={isMobile ? "4" : "3"}
              bg="white"
              zIndex={1}
              h="16">
              <HStack bg="white" w="100%" justify="space-between" mt="6">
                <Box flex={1}>
                  <Input
                    size={useBreakpointValue({ base: "md", lg: "lg" })}
                    fontSize={useBreakpointValue({
                      base: "1.5rem",
                      lg: "2.5rem",
                    })}
                    borderBottom="none"
                    variant="flushed"
                    _focus={{ borderBottom: "none" }}
                    _focusVisible={{ borderBottom: "none" }}
                    fontWeight="bold"
                    placeholder="Enter a title"
                    minW="80%"
                    isDisabled={isMutating || isFetching}
                    onChange={(e) => setTitle(e.target.value)}
                    value={title}
                  />
                </Box>
              </HStack>
            </Box>
            <Box
              px={isMobile ? "4" : "3"}
              py="2"
              m="auto"
              maxW="container.lg"
              fontSize="1rem"
              className="manual-rte">
              <EditorContent editor={editor} />
            </Box>
          </Box>
        </Box>

        {!isMobile && (
          <Box
            h="calc(100vh - 48px)"
            pos="fixed"
            top="calc(var(--chakra-space-top-bar) + 1rem)"
            right="2"
            pl="2"
            w="320px">
            {sectionQuery.isSuccess && (
              <VStack spacing="4" w="100%" align="stretch">
                <ManualSectionDetailsCard sectionID={sectionID} />
                <Card variant="outline">
                  <CardHeader>
                    <Heading size="sm">Usage</Heading>
                  </CardHeader>
                  {isLoading ? (
                    <CardBody>
                      <SkeletonText
                        mt="2"
                        noOfLines={3}
                        spacing="2"
                        skeletonHeight="3"
                      />
                    </CardBody>
                  ) : (
                    linkedQuery.isSuccess &&
                    (linkedQuery.data.linkedRecords.length > 0 ? (
                      <CardBody>
                        <UsageManualSectionsLink sectionID={sectionID} />
                      </CardBody>
                    ) : (
                      <CardBody>No linked records.</CardBody>
                    ))
                  )}
                </Card>
              </VStack>
            )}
          </Box>
        )}

        <Drawer
          isOpen={drawerDisclosure.isOpen}
          placement="right"
          onClose={drawerDisclosure.onClose}
          size="md">
          <DrawerOverlay />
          <chakra-scope>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>Section Details</DrawerHeader>
              <DrawerBody p={0}>
                <VStack spacing={4} align="stretch">
                  {sectionQuery.isSuccess && (
                    <Box px={6}>
                      <ManualSectionDetailsCard sectionID={sectionID} />
                    </Box>
                  )}
                  <Box px={6}>
                    <Card variant="outline">
                      <CardHeader>
                        <Heading size="sm">Usage</Heading>
                      </CardHeader>
                      {isLoading ? (
                        <CardBody>
                          <SkeletonText
                            mt="2"
                            noOfLines={3}
                            spacing="2"
                            skeletonHeight="3"
                          />
                        </CardBody>
                      ) : (
                        linkedQuery.isSuccess &&
                        (linkedQuery.data.linkedRecords.length > 0 ? (
                          <CardBody>
                            <UsageManualSectionsLink sectionID={sectionID} />
                          </CardBody>
                        ) : (
                          <CardBody>No linked records.</CardBody>
                        ))
                      )}
                    </Card>
                  </Box>
                </VStack>
              </DrawerBody>
            </DrawerContent>
          </chakra-scope>
        </Drawer>
      </Box>
    </chakra-scope>
  );
}
